import Box from '@mui/material/Box/Box';
import {
    CellClickedEvent,
    FilterModifiedEvent,
    SortChangedEvent,
    ValueFormatterParams
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import { useObservable, useSubscription } from 'observable-hooks';
import { useState } from 'react';
import { connect } from 'react-redux';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { ActivityLogDto } from '../../common/PricePrgClient';
import { RootState } from '../../store';
import { ActivityLogDetailsDialog } from './ActivityLogDetailsDialog';
import SelectFilterComponent from './SelectFilterComponent';
import { getActivityLogs } from './redux/activityLogSelectors';

function mapStateToProps(state: RootState) {
    return {
        logs: getActivityLogs(state)
    };
}

type OwnProps = {
    onSort(column: string, sortOrder: 'asc' | 'desc'): void;
    onFilterChanged(filter: ActivityLogFilter): void;
};

export type ActivityLogFilter = {
    id: string;
    type: string;
    action: string;
    createdBy: string;
    createdDate: string;
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ActivityLogTable = ({ logs, onFilterChanged, onSort }: Props) => {
    const [selectedActivityLog, setSelectedActivityLog] = useState<ActivityLogDto | null>(null);
    const [filters, setFilters] = useState<ActivityLogFilter | null>(null);

    useSubscription(
        useObservable(
            filters$ =>
                filters$.pipe(
                    distinctUntilChanged(),
                    debounceTime(300),
                    tap(([f]) => {
                        if (f != null) {
                            onFilterChanged({
                                id: f.id?.replaceAll('|', '') ?? '',
                                action: f.action?.replaceAll('|', '') ?? '',
                                createdBy: f.createdBy?.replaceAll('|', '') ?? '',
                                createdDate: f.createdDate?.replaceAll('|', '') ?? '',
                                type: f.type?.replaceAll('|', '') ?? ''
                            });
                        }
                    })
                ),
            [filters]
        )
    );

    const setupGrid = () => {
        const filterInputs = document.querySelectorAll('.ag-floating-filter-input input');
        for (const obj of Array.from(filterInputs)) {
            if (obj.attributes.getNamedItem('disabled') != null) {
                // skip columns with disabled filter
                return;
            }
            obj.setAttribute('placeholder', 'Filter');
        }
    };

    function dateFormatter(params: ValueFormatterParams) {
        return moment(params.value).format('yyyy-MM-DD HH:mm');
    }

    const handleFilterModel = (filterEvent: FilterModifiedEvent<ActivityLogDto>) => {
        const filterValue = filterEvent.filterInstance.getModel()?.filter ?? '';
        const columnName = filterEvent.column.getColId();
        const newFilter = { ...filters };
        newFilter[columnName] = filterValue;
        setFilters(newFilter as ActivityLogFilter);
    };

    const handleOnSort = (sort: SortChangedEvent<ActivityLogDto>) => {
        const columnSorted = sort.columnApi.getColumnState().find(x => x.sort != null);
        const columnId = columnSorted?.colId ?? '';
        console.log('columnid: ' + columnId);
        console.log('', columnSorted?.sort);
        onSort(columnId, columnSorted?.sort ?? 'asc');
    };

    const defaultColDef = {
        filter: true,
        sortable: true,
        resizable: true,
        editable: false,
        floatingFilter: true,
        floatingFilterComponentParams: {
            suppressFilterButton: true
        }
    };

    const columnDefs = [
        // filter not specified, defaultColDef setting is used
        {
            field: 'id',
            headerName: '#',
            width: 100,
            cellStyle: { color: 'blue', textDecoration: 'underline', cursor: 'pointer' }
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 200,
            floatingFilterComponent: SelectFilterComponent
        },
        {
            field: 'action',
            headerName: 'Action',
            width: 500
        },
        {
            field: 'createdBy',
            headerName: 'Created by',
            width: 350
        },
        {
            field: 'createdDate',
            headerName: 'Created',
            width: 200,
            valueFormatter: dateFormatter
        }
    ];

    const onCellClicked = (params: CellClickedEvent) => {
        if (params.column.getId() == 'id') {
            setSelectedActivityLog(logs.find(x => x.id === params.value) ?? null);
        }
    };

    return (
        <Box style={{ height: '82vh' }}>
            <AgGridReact
                className="ag-theme-alpine"
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowData={logs}
                onCellClicked={onCellClicked}
                onFirstDataRendered={setupGrid}
                floatingFiltersHeight={30}
                containerStyle={{
                    height: '80vh',
                    // Always show y-scroll because it fixes flickering issues
                    overflowY: 'scroll'
                }}
                onFilterModified={handleFilterModel} //{(event: FilterModifiedEvent<ActivityLogDto>) => { const filtermodel = event.filterInstance.getModel(); console.log(event);}}
                onSortChanged={handleOnSort} // {(event: SortChangedEvent<ActivityLogDto[]>) => { console.log(event);}}
            />
            {selectedActivityLog != null && (
                <ActivityLogDetailsDialog
                    onClose={() => setSelectedActivityLog(null)}
                    activityLog={selectedActivityLog}
                />
            )}
        </Box>
    );
};

export default connect(mapStateToProps)(ActivityLogTable);
